<template>
  <base-message :message="message" :full-width="true" :fullscreen="fullscreen">
    <div class="message-body is-block code-message">
      <div class="message-header">
        <label><b>{{ problem.name }}</b></label>
      </div>

      <div class="message-block-content">
        <editor-content :content="problem.content"></editor-content>

        <problem-attachments
            :problem="problem"
            @download-attachment="downloadAttachment"></problem-attachments>
        <div v-if="$isDataLoaded(mainFile)" class="code-editor">
          <div v-if="problem.template_readonly">
            <code-blanks :ref="'code-blanks-' + _uid"
                         v-model="code"
                         :language="problem.language"
                         :template="problem.template"></code-blanks>
          </div>
          <ace-editor
              v-else
              v-model="code"
              :auto-height="true"
              :min-lines="6"
              :hash="mainFile.srcurl"
              :run="run"
              :show-placeholder="!code"
              placeholder="在此编写你的代码"></ace-editor>
        </div>

        <div class="buttons">
          <button class="button is-small is-light"
                  @click="run"
                  :disabled="shell.status === 'running'">
            <v-color-icon v-if="shell.status !== 'running'" name="play"></v-color-icon>
            <v-color-icon v-else name="loader" :spin="true"></v-color-icon>
            <span v-show="shell.status !== 'running'">运行</span>
            <span v-show="shell.status === 'running'">运行中</span>
          </button>
          <button v-if="!skip" class="button is-white is-small"
                  @click="next">跳过本题
          </button>

          <div class="flex-pull-right">
            <button v-if="problem.template && !problem.template_readonly" class="button is-white is-small"
                    @click="code = problem.template">还原模板
            </button>

            <template v-if="IClassroom.deskmate">
              <button class="tooltip-trigger button is-white is-small"
                      @click="spy">
                偷看同桌代码
              </button>
              <div v-show="showDeskmateCode" class="deskmate-code">
                <img :src="IClassroom.deskmate.head_image" class="user-head" alt="">
                <ace-editor v-if="$isDataLoaded(deskmateCode)"
                            :read-only="true"
                            :height="'300px'"
                            :value="deskmateCode.code || '同桌表示自己也没写 ...'"></ace-editor>
                <div v-else>正在偷看 ...</div>
              </div>
            </template>
            <button v-if="showAnswer !== ''"
                    class="tooltip-trigger button is-white is-small focus-wave"
                    @click="showAnswer = !showAnswer">
              查看答案
            </button>
            <div v-show="showAnswer" class="deskmate-code">
              <editor-content :content="problem.explanation_content" class="content"></editor-content>
            </div>


          </div>
        </div>
      </div>

      <testcases v-show="shell.status !== 'init'" :problem="problem" :ref="'testcases-' + _uid"></testcases>


    </div>
  </base-message>
</template>

<script>
import IClassroom from '../lib/IClassroom.js'
import BaseMessage from './BaseMessage'
import AceEditor from '@/common/ace_editor/AceEditor'
import Testcases from '@/components/classroom/messages/partials/Testcases'
import ProblemAttachments from '@/components/classroom/messages/partials/Attachments'
import EditorContent from '@/common/tinymce/EditorContent'
import CodeBlanks from '@/components/classroom/messages/partials/CodeBlanks'

export default {
  components: { CodeBlanks, EditorContent, BaseMessage, AceEditor, Testcases, ProblemAttachments },
  props: ['message'],
  data () {
    return {
      code: '',
      codeBefore: '',
      codeAfter: '',
      skip: false,
      python: null,
      fullscreen: false,
      IClassroom: IClassroom,
      showDeskmateCode: false,
      showAnswer: '',
      isScored: false,
      shell: {
        status: 'init',
        output: '',
        error: '',
        startAt: 0,
        endAt: 0,
        timer: null
      }
    }
  },
  computed: {
    mainFile () {
      if (this.$isDataLoaded(this.problem)) {
        this.code = ''
        let file = this.$api.getData(`/student/courses/${IClassroom.course._id}/groups/${IClassroom.group._id}/problems/${this.message.data.problem}/code`)
        if (this.$isDataLoaded(file)) {
          if (file.code) {
            this.code = file.code
          } else {
            this.code = ''
            if (this.problem.template && !this.problem.template_readonly) {
              this.code = this.problem.template
            }
          }
          this.attachments = file.attachments || []
        }
        return file
      }
      return this.$getDataState('loading')
    },
    problem () {
      const problem = this.$api.getData(`/student/courses/${IClassroom.course._id}/groups/${IClassroom.group._id}/problems/${this.message.data.problem}`)
      if (this.$isDataLoaded(problem)) {
        if (problem.record && problem.record.oj_status && problem.record.oj_status.length) {
          problem.record.oj_status.forEach(status => {
            const testcase = problem.testcases.find({ '_id': status._id })
            if (testcase && !testcase.exec_time) {
              this.$set(testcase, 'status', status.status)
              status.static_score && this.$set(testcase, 'static_score', status.static_score)
              this.$set(testcase, 'exec_time', status.time)
            }
          })
        }
        if (problem.testcases) {
          problem.staticTestcases = problem.testcases.filter((testcase) => {
            return testcase.type === 'static'
          })
          problem.dynamicTestcases = problem.testcases.filter((testcase) => {
            return !testcase.type || testcase.type === 'dynamic'
          })
          problem.styleTestcases = problem.testcases.filter((testcase) => {
            return testcase.type === 'style'
          })
        }
        if (problem.template_readonly) {
          const templates = problem.template.split('#####')
          this.codeBefore = templates[0].trim()
          this.codeAfter = (templates[1] || '').trim()
        }
      }
      return problem
    },
    deskmateCode () {
      return this.$api.getData(`/student/courses/${IClassroom.course._id}/groups/${IClassroom.group._id}/deskmate/${IClassroom.deskmate.user_id}/problems/${this.message.data.problem}/code`)
    }
  },
  mounted () {
    this.showAnswer = ''
    this.showDeskmateCode = false
  },
  methods: {
    spy () {
      this.showDeskmateCode = !this.showDeskmateCode
      IClassroom.showUserBubble(0, ':spy')
    },
    async downloadAttachment (attachment) {
      await this.$api.downloadFile(`${this.problem.srcurl}/attachments/${attachment._id}`, attachment.name)
    },
    async run () {
      this.showAnswer = false
      if (!this.code) {
        this.$noty.warning('没有可以运行的代码')
        return
      }
      try {
        this.shell.status = 'running'
        clearTimeout(this.shell.timer)
        this.shell.timer = setTimeout(() => {
          // 最多等待十秒,如果无响应则停止运行
          this.$noty.warning('网络连接异常')
          this.shell.status = 'stop'
        }, 10000)

        await this.$refs['testcases-' + this._uid].runAllTestcase(this.code, () => {
          // 运行结束，有可能不进入此回调
          setTimeout(async () => {
            this.shell.status = 'stop'
            clearTimeout(this.shell.timer)

            const ok = this.problem.testcases.reduce((ret, test) => {
              return ret && test.status && test.status === 200
            }, true)

            IClassroom.showUserBubble(0, ok ? 'check' : 'close', false, 8000)

            if (ok) {
              await this.checkAnswer()
              IClassroom.addScore(ok ? 100 : 0, false)
              this.next()
            }
          }, 500)
        })
      } catch (e) {
        // pass
      }
    },
    async checkAnswer () {
      for (let i = 1; i < IClassroom.classmates.length; i++) {
        const user = IClassroom.classmates[i]
        let ok = true
        ok &= user.problems && user.problems[this.problem._id] && user.problems[this.problem._id].score === 10
        if (ok && !this.isScored) {
          IClassroom.addScore(ok ? 50 : 0, user.isOpponent)
        }
        IClassroom.showUserBubble(i, ok ? 'check' : 'close', false, 8000)
        await new Promise((resolve => {
          setTimeout(resolve, 30)
        }))
      }

      this.isScored = true
    },
    next () {
      if (!this.isScored) {
        this.checkAnswer()
      }
      this.showAnswer = false
      if (this.skip) return
      this.skip = true
      IClassroom.requestNextMessage(0)
    }
  }
}
</script>

<style lang="stylus">
.message-content-wrapper {
  .trigger {
    width 100%
  }
}
</style>
<style lang="stylus" scoped>
.code-message {
  width 100%
  min-width 200px
}

.deskmate-code {
  width 60%
  max-width 80vw
  max-height 400px
  overflow auto
  font-family $fontCode
  box-shadow $cardShadow
  background $neBg
  position absolute
  right 0
  z-index 2

  .user-head {
    width 36px
    height 36px
    border 2px solid #dddddd
    border-radius 50%
    overflow hidden
    margin 5px 10px
  }
}

.v-popover {
  display inline-block
}

.turtle-target {
  background #f5f5f5
}

.shell {
  margin-top .5em
}

.code-editor {
  border-top 1px solid #dddddd
  border-bottom 1px solid #dddddd
  margin .5em -.75em 0 -.75em
}

.buttons {
  padding-top .75rem
}


</style>
